import React from 'react';
import { css, cx, injectGlobal } from '@emotion/css';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import { ROOT_NODE, useEditor, useNode } from '@craftjs/core';
import { useRenderContext, type FormHostConfig } from '../../context/RenderContext';
import type { TRBLNumberCOORDS } from '../../types/layout';
import type { NodeCustomProps } from '../../types/craft';
import {
  DEFAULT_VISIBILITY,
  getBoxShadow,
  getBorderRadiusStyle,
  getBorderStyles,
  getGlobalStyleColor,
  getMarginStyle,
  getPaddingStyle,
  getPositionPropsV2,
  getPositionStyle,
  getVisibilityClasses,
  getMediaQueryHeader,
} from '../../utils/styleHelpers';
import { FORM_NODE_NAME } from '../../utils/constants';
import { computeFormFieldDescendantStyles } from './fields/fieldStyles';
import { FontManager } from '../../fonts/manager';
import { generateUuidV4 } from './form-utils';
import { getBreakpointProp } from '../../utils/breakpointUtils';
import type { StylesConfig } from '../../types/styles';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getHoverStyles,
} from '../../utils/animationHelpers';
import { FormPropsDto, type ActionType } from '../../dto/craft/props/FormPropsDto';

export type FormProps = FormPropsDto;

export type { ActionType };

export const defaultProps: Partial<FormProps> = {
  id: '',
  name: '',
  // Field*-prefixed defaults (used by fields)
  followupAction: { type: 'remain' },
  fieldWidth: '100%',
  fieldMaxWidth: '100%',
  fieldMinWidth: '0px',
  fieldMargin: [0, 0, 16, 0],
  fieldBorderRadius: [6, 6, 6, 6],
  fieldInputPadding: [6, 8, 6, 8],

  fieldLabelFontFamily: 'Inter',
  fieldLabelFontSize: '10px',
  fieldLabelFontWeight: 500,
  fieldLabelColor: { r: 87, g: 95, b: 106, a: 1 },
  fieldLabelInvalidColor: { r: 234, g: 101, b: 89, a: 1 },
  fieldLabelSpacing: 0,
  fieldInputFontFamily: 'Inter',
  fieldInputFontSize: '12px',
  fieldInputFontWeight: 500,
  fieldInputTextColor: { r: 107, g: 114, b: 128, a: 1 },
  fieldInputHoverTextColor: { r: 0, g: 0, b: 0, a: 1 },
  fieldInputSpacing: 0,
  fieldInputBackgroundColor: { r: 255, g: 255, b: 255, a: 1 },
  fieldInputBorderColor: { r: 229, g: 231, b: 235, a: 1 },
  fieldInputBorderWidth: 1,
  fieldInputHoverBackgroundColor: { r: 243, g: 245, b: 247, a: 1 },
  fieldInputHoverBorderColor: { r: 146, g: 127, b: 239, a: 1 },
  fieldInputInvalidBackgroundColor: { r: 255, g: 255, b: 255, a: 1 },
  fieldInputInvalidBorderColor: { r: 234, g: 101, b: 89, a: 1 },
  fieldHelperTextColor: { r: 156, g: 163, b: 175, a: 1 },
  fieldHelperTextInvalidColor: { r: 234, g: 101, b: 89, a: 1 },
  fieldCheckboxCheckedColor: { r: 98, g: 69, b: 242, a: 1 }, // indigo-500
  fieldShadowType: 'none',
  fieldShadowSettings: { x: 0, y: 0, blur: 0, spread: 0, color: '#000000' },
  width: '100%',
  fillSpace: 'yes',
  flexBasis: '100%',
  height: 'auto',
  minWidth: 100,
  minHeight: 64,
  maxWidthUnit: 'px',
  maxWidth: 1200,
  padding: [16, 16, 16, 16],
  margin: [0, 0, 0, 0],
  borderRadius: [8, 8, 8, 8],
  borderWidth: 1,
  borderColor: { r: 229, g: 231, b: 235, a: 1 }, // neutral border
  hoverBorderColor: undefined,
  backgroundColor: undefined,
  position: 'relative',
  top: '0px',
  left: '0px',
  right: undefined,
  bottom: undefined,
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  zIndex: 0,
  visibility: { ...DEFAULT_VISIBILITY },
  aspectRatioLock: false,
  aspectRatio: undefined,
  tablet: {},
  mobile: {
    width: '100%',
    fillSpace: 'no',
    flexBasis: '100%',
  },
};

const injectFormFonts = (
  props: Partial<FormProps>,
  fontManager: FontManager,
  breakpoint: 'desktop' | 'tablet' | 'mobile',
) => {
  // Add label font
  const fieldLabelFontFamily = getBreakpointProp(props, 'fieldLabelFontFamily', breakpoint);
  const fieldLabelFontWeight = getBreakpointProp(props, 'fieldLabelFontWeight', breakpoint);

  if (fieldLabelFontFamily) {
    const css = fontManager.getFontFaceCss(
      fieldLabelFontFamily,
      String(fieldLabelFontWeight || 500),
      'normal',
    );
    injectGlobal(css);
  }

  // Add input font
  const fieldInputFontFamily = getBreakpointProp(props, 'fieldInputFontFamily', breakpoint);
  const fieldInputFontWeight = getBreakpointProp(props, 'fieldInputFontWeight', breakpoint);

  if (fieldInputFontFamily) {
    const css = fontManager.getFontFaceCss(
      fieldInputFontFamily,
      String(fieldInputFontWeight || 500),
      'normal',
    );
    injectGlobal(css);
  }
};

// Plain object styles for the outer node wrapper (used by SSR and editor wrapper)
export const getFormNodeStyles = ({
  height,
  width,
  maxWidth,
  maxWidthUnit,
  minHeight,
  margin,
  position,
  top,
  left,
  right,
  bottom,
  positionAnchorsState,
  zIndex,
  aspectRatioLock,
  aspectRatio,
  fillSpace,
  flexBasis,
}: Partial<FormProps>) => {
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const marginString = margin
    ? getMarginStyle((margin || [0, 0, 0, 0]) as TRBLNumberCOORDS).margin
    : undefined;

  const isAspectRatioLocked = aspectRatioLock && aspectRatio && aspectRatio > 0;
  const aspectRatioValue = aspectRatio ? `${aspectRatio} / 1` : undefined;

  // Use maxWidthUnit if provided, default to '%' for backward compatibility with existing data
  // New components will have maxWidthUnit: 'px' from defaultProps
  const unit = maxWidthUnit || '%';
  const maxWidthValue = maxWidth ? `${maxWidth}${unit}` : undefined;

  let flex: string | undefined;
  if (fillSpace) {
    if (fillSpace === 'yes') {
      if (flexBasis) {
        flex = `1 1 ${flexBasis}`;
      }
    } else {
      flex = 'unset';
    }
  }

  return {
    flex,
    position: positionStyle.position || 'relative',
    padding: marginString,
    ...positionStyle,
    zIndex: zIndex || undefined,
    ...(minHeight && { minHeight: `${minHeight}px` }),
    width: width || undefined,
    height: isAspectRatioLocked ? 'auto' : height || undefined,
    maxWidth: maxWidthValue,
    ...(isAspectRatioLocked && aspectRatioValue ? { aspectRatio: aspectRatioValue } : {}),
  };
};

/**
 * Returns plain style object with base + mobile + tablet for editor breakpoint merge (like Image).
 */
export function getFormNodeStylesObject(props: Partial<FormProps>) {
  const baseStyle = getFormNodeStyles(props);
  const finalStyle = {
    ...baseStyle,
    display: 'flex',
    mobile: {} as ReturnType<typeof getFormNodeStyles>,
    tablet: {} as ReturnType<typeof getFormNodeStyles>,
  };

  const { mobile, tablet } = props;
  if (mobile && Object.keys(mobile).length) {
    const positionProps = getPositionPropsV2(mobile as Partial<FormProps>, props);
    finalStyle.mobile = getFormNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<FormProps>);
  }
  if (tablet && Object.keys(tablet).length) {
    const positionProps = getPositionPropsV2(tablet as Partial<FormProps>, props);
    finalStyle.tablet = getFormNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<FormProps>);
  }

  return finalStyle;
}

export const getFormNodeStyleClass = (props: Partial<FormProps>, isServerRendering: boolean) => {
  const { mobile, tablet, ...currentBpStyles } = getFormNodeStylesObject(props);

  const finalStyle: CSSInterpolation = {
    ...currentBpStyles,
    overflow: 'visible', // Allow dropdowns to overflow
  };
  if (mobile) {
    finalStyle[getMediaQueryHeader(isServerRendering, 'mobile')] = mobile;
  }
  if (tablet) {
    finalStyle[getMediaQueryHeader(isServerRendering, 'tablet')] = tablet;
  }

  return css(finalStyle);
};

// Inner content wrapper styles
export const computeFormContentStyles = (
  {
    padding,
    borderRadius,
    borderWidth,
    borderColor,
    hoverBorderColor,
    backgroundColor,
    hoverEffect,
    hoverTransitionDuration,
    hoverIntensity,
    shadowType,
    shadowSettings,
  }: Partial<FormProps>,
  activeGlobalStyles: StylesConfig,
) => {
  const paddingString = getPaddingStyle((padding || [0, 0, 0, 0]) as TRBLNumberCOORDS).padding;
  const borderRadiusString = getBorderRadiusStyle(
    (borderRadius || [0, 0, 0, 0]) as TRBLNumberCOORDS,
  ).borderRadius;
  const borderStyles = getBorderStyles(borderWidth || 0, borderColor, activeGlobalStyles.colors);
  const hoverBorderColorString = getGlobalStyleColor(hoverBorderColor, activeGlobalStyles.colors);
  const backgroundColorString = getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors);

  const hoverStylesObj = getHoverStyles(
    hoverEffect,
    hoverTransitionDuration,
    hoverIntensity,
    !!getBoxShadow(shadowType, shadowSettings),
  );
  const existingHover = hoverBorderColorString ? { borderColor: hoverBorderColorString } : {};
  const hoverEffectHover = (hoverStylesObj as Record<string, unknown>)['&:hover'] as
    | Record<string, unknown>
    | undefined;
  const mergedHover = { ...existingHover, ...hoverEffectHover };

  return {
    padding: paddingString,
    borderRadius: borderRadiusString,
    backgroundColor: backgroundColorString || 'transparent',
    borderColor: borderStyles.borderColor,
    borderStyle: borderStyles.borderStyle,
    borderWidth: borderStyles.borderWidth,
    ...(hoverStylesObj.transition ? { transition: hoverStylesObj.transition } : {}),
    ...(Object.keys(mergedHover).length > 0 && {
      '&:hover': mergedHover,
      boxShadow: getBoxShadow(shadowType, shadowSettings),
    }),
  };
};

const getFormContentStyle = (
  props: Partial<FormProps>,
  isServerRendering: boolean,
  activeGlobalStyles: StylesConfig,
) => {
  const current = computeFormContentStyles(props, activeGlobalStyles);
  const finalStyle: Record<string, CSSInterpolation> = {
    ...current,
    width: '100%',
    height: '100%',
    pointerEvents: 'auto',
    overflow: 'visible', // Allow dropdowns to overflow
  };

  const { mobile, tablet } = props;
  if (mobile && Object.keys(mobile).length) {
    finalStyle[getMediaQueryHeader(isServerRendering, 'mobile')] = computeFormContentStyles(
      {
        ...props,
        ...mobile,
      } as Partial<FormProps>,
      activeGlobalStyles,
    ) as CSSObject;
  }
  if (tablet && Object.keys(tablet).length) {
    finalStyle[getMediaQueryHeader(isServerRendering, 'tablet')] = computeFormContentStyles(
      {
        ...props,
        ...tablet,
      } as Partial<FormProps>,
      activeGlobalStyles,
    ) as CSSObject;
  }

  return css(finalStyle);
};

/**
 * Get form hosts from context or use default values.
 * Form host values are only available in server environment.
 *
 * @param formHosts - Form host configuration
 * @returns Form host configuration
 */
const getFormHosts = (formHosts: FormHostConfig | undefined) => {
  let stargateUrl = '';
  let optinBackupUrl = '';
  let defaultThankYouUrl = '';
  if (formHosts) {
    stargateUrl = formHosts.stargateUrl;
    optinBackupUrl = formHosts.optinBackupUrl;
    defaultThankYouUrl = formHosts.defaultThankYouUrl;
  }
  return { stargateUrl, optinBackupUrl, defaultThankYouUrl };
};

const Form = (props: Partial<FormProps> & { children?: React.ReactNode }) => {
  props = { ...defaultProps, ...props };

  const {
    isEditable,
    environment,
    fontManager,
    styles: activeGlobalStyles,
    formHosts,
  } = useRenderContext();
  const { id } = useNode((node) => ({
    id: node.id,
  }));

  const { actions: editorActions, breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  let formId = getBreakpointProp(props, 'id', breakpoint);
  if (!formId) {
    const generatedFormId = generateUuidV4();
    editorActions.history.ignore().setProp(id, (props: FormProps) => {
      props.id = generatedFormId;
    });
    props.id = generatedFormId;
    formId = generatedFormId;
  }

  const { stargateUrl, optinBackupUrl, defaultThankYouUrl } = getFormHosts(formHosts);
  const visibilityClasses = getVisibilityClasses(props.visibility || DEFAULT_VISIBILITY);
  const isServerRender = environment === 'server';
  const renderNodeWrapper = isServerRender || environment === 'inspiration-preview';

  // Generate descendant selector styles for all child fields
  const fieldStyles = computeFormFieldDescendantStyles(props, isServerRender, activeGlobalStyles);

  const formName = getBreakpointProp(props, 'name', breakpoint);
  const followupAction = getBreakpointProp(props, 'followupAction', breakpoint);

  injectFormFonts(props, fontManager, breakpoint);

  const isBuilderMode = environment === 'builder';

  const animationClass = getAnimationInitialClass(props.animationType, environment);
  const animationAttrs = getAnimationDataAttributes(props);

  const content = (
    <form
      className={cx(
        'form-content',
        isEditable && 'node-dashed-indicator boundary-indicator styled-node',
        !isServerRender && animationClass,
        getFormContentStyle(props, isServerRender, activeGlobalStyles),
        fieldStyles,
      )}
      name={formName || `form-${formId}`}
      action={`${stargateUrl}/integration/v2/forms/${formId}/submissions`}
      data-backup-action={`${optinBackupUrl}/integration/v2/forms/${formId}/submissions`}
      method="POST"
      acceptCharset="UTF-8"
      target="_top"
      onSubmit={(e): void => {
        // In builder/preview we must never navigate or submit
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDownCapture={(e) => {
        // Block Enter key default form submission in non-server environments
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      autoComplete={isEditable ? 'off' : 'on'}
      noValidate
      data-form-id={formId}
      data-form-version="2"
      // Follow-up action after successful submission
      data-action-type={followupAction?.type || 'remain'}
      data-action-url={followupAction?.url || defaultThankYouUrl}
      {...(!isServerRender ? animationAttrs : {})}
      {...(isBuilderMode ? { 'data-animation-preview': id } : {})}
    >
      <div
        className={css`
          position: relative;
          z-index: 5;
          display: flex;
          width: 100%;
          height: 100%;
          flex-direction: column; /* Field and submit button sizing & spacing handled in fieldStyles */
        `}
      >
        {props.children}
      </div>
    </form>
  );

  // Wrap with form-node div for server and inspiration-preview rendering
  // (in the editor, the Resizer component provides this wrapper instead)
  if (renderNodeWrapper) {
    return (
      <div
        className={cx(
          'form-node',
          visibilityClasses,
          animationClass,
          getFormNodeStyleClass(props, isServerRender),
        )}
        {...animationAttrs}
      >
        {content}
      </div>
    );
  }

  return content;
};

Form.craft = {
  displayName: FORM_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: FORM_NODE_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

export default Form;

export { default as FormTextField } from './fields/FormTextField';
export { defaultProps as formTextFieldDefaultProps } from './fields/FormTextField';
export type { FormTextFieldProps } from './fields/FormTextField';
export { default as FormEmailField } from './fields/FormEmailField';
export { defaultProps as formEmailFieldDefaultProps } from './fields/FormEmailField';
export type { FormEmailFieldProps } from './fields/FormEmailField';
export { default as FormConsentField } from './fields/FormConsentField';
export { defaultProps as formConsentFieldDefaultProps } from './fields/FormConsentField';
export type { FormConsentFieldProps } from './fields/FormConsentField';
export { default as FormDropdownField } from './fields/FormDropdownField';
export { defaultProps as formDropdownFieldDefaultProps } from './fields/FormDropdownField';
export type { FormDropdownFieldProps } from './fields/FormDropdownField';
export { default as FormPhoneField } from './fields/FormPhoneField';
export { defaultProps as formPhoneFieldDefaultProps } from './fields/FormPhoneField';
export type { FormPhoneFieldProps } from './fields/FormPhoneField';
export { default as FormSelectionField } from './fields/FormSelectionField';
export { defaultProps as formSelectionFieldDefaultProps } from './fields/FormSelectionField';
export type { FormSelectionFieldProps } from './fields/FormSelectionField';
export { default as FormSubmitButton } from './buttons/FormSubmitButton';
export type { FormSubmitButtonProps } from './buttons/FormSubmitButton';
